/**
 * SidebarDrawer - A custom web component for creating sidebar drawers
 * 
 * Usage:
 * <sidebar-drawer id="cart-drawer" position="right">
 *   <div slot="header">Cart</div> <!-- Optional header content -->
 *   <div>Content goes here</div> <!-- No slot keyword needed for body -->
 *   <div slot="footer">Footer content</div> <!-- Optional footer content -->
 * </sidebar-drawer>
 * 
 * <!-- Open the drawer -->
 * <button onclick="document.getElementById('cart-drawer').open()">Open Drawer</button>
 */

class SidebarDrawer extends HTMLElement {
  constructor() {
    super();
    
    // Create a shadow DOM
    this.attachShadow({ mode: 'open' });
    
    // Define drawer properties with default values
    this.position = this.getAttribute('position') || 'right';
    this.drawerWidth = this.getAttribute('width') || '20rem';
    this.showOverlay = this.hasAttribute('show-overlay') ? this.getAttribute('show-overlay') !== 'false' : true;
    this.lockBodyScroll = this.hasAttribute('lock-body-scroll') ? this.getAttribute('lock-body-scroll') !== 'false' : true;
    
    // Initialize the drawer
    this._createDrawer();
    this._setupEventListeners();
  }
  
  // Observed attributes for reactivity
  static get observedAttributes() {
    return ['position', 'width', 'show-overlay', 'lock-body-scroll', 'open'];
  }
  
  // Lifecycle: when attributes change
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    
    switch (name) {
      case 'position':
        this.position = newValue || 'right';
        this._updateStyles();
        break;
      case 'width':
        this.drawerWidth = newValue || '25rem';
        this._updateStyles();
        break;
      case 'show-overlay':
        this.showOverlay = newValue !== 'false';
        this._updateStyles();
        break;
      case 'lock-body-scroll':
        this.lockBodyScroll = newValue !== 'false';
        break;
      case 'open':
        if (newValue === null) {
          this.close();
        } else {
          this.open();
        }
        break;
    }
  }
  
  // Lifecycle: when component is connected to the DOM
  connectedCallback() {
    // Check if it should be open by default
    if (this.hasAttribute('open')) {
      this.open();
    }
  }
  
  // Lifecycle: when component is disconnected from the DOM
  disconnectedCallback() {
    // Clean up any event listeners or references
    if (this.closeBtn) {
      this.closeBtn.removeEventListener('click', this._handleClose);
    }
    if (this.overlay) {
      this.overlay.removeEventListener('click', this._handleClose);
    }
  }
  
  // Create the drawer layout
  _createDrawer() {
    // Create the drawer elements
    const container = document.createElement('div');
    container.className = 'drawer-container';
    
    const content = document.createElement('div');
    content.className = 'drawer-content';
    content.setAttribute('role', 'dialog');
    
    // Create header section with slot
    const headerSection = document.createElement('div');
    headerSection.className = 'drawer-header';
    const headerSlot = document.createElement('slot');
    headerSlot.name = 'header';
    headerSection.appendChild(headerSlot);
    
    // Create body section with slot
    const bodySection = document.createElement('div');
    bodySection.className = 'drawer-body';
    const bodySlot = document.createElement('slot');
    bodySection.appendChild(bodySlot);
    
    // Create footer section with slot
    const footerSection = document.createElement('div');
    footerSection.className = 'drawer-footer';
    const footerSlot = document.createElement('slot');
    footerSlot.name = 'footer';
    footerSection.appendChild(footerSlot);
    
    // Create default close button
    this.closeBtn = document.createElement('button');
    this.closeBtn.className = 'drawer-close-btn';
    this.closeBtn.innerHTML = '&#10006;';
    this.closeBtn.setAttribute('aria-label', 'Close');
    headerSection.appendChild(this.closeBtn);
    
    content.appendChild(headerSection);
    content.appendChild(bodySection);
    content.appendChild(footerSection);
    
    // Create overlay if needed
    if (this.showOverlay) {
      this.overlay = document.createElement('div');
      this.overlay.className = 'drawer-overlay';
      this.overlay.setAttribute('role', 'presentation');
      container.appendChild(this.overlay);
    }
    
    container.appendChild(content);
    
    // Create and attach the styles
    const style = document.createElement('style');
    style.textContent = this._getStyles();
    
    // Append everything to the shadow DOM
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(container);
    
    // Store references
    this.container = container;
    this.contentElement = content;
  }
  
  // Set up event listeners
  _setupEventListeners() {
    // Close button event
    this._handleClose = () => this.close();
    
    // Add event listener to the default close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', this._handleClose);
    }
    
    // When the overlay is clicked
    if (this.overlay) {
      this.overlay.addEventListener('click', this._handleClose);
    }
    
    // Prevent clicks inside drawer from closing it
    this.contentElement.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  }
  
  // Update styles when props change
  _updateStyles() {
    const styleElement = this.shadowRoot.querySelector('style');
    styleElement.textContent = this._getStyles();
  }
  
  // Generate the styles based on props
  _getStyles() {
    return `
      :host {
        display: block;
        visibility: hidden;
        position: fixed;
        z-index: 9999;
        inset: 0;
        pointer-events: none;
        transition: visibility 0.3s;
      }
      
      :host([open]) {
        visibility: visible;
        pointer-events: all;
      }
      
      .drawer-container {
        position: absolute;
        inset: 0;
        display: flex;
        justify-content: ${this.position === 'left' ? 'flex-start' : 'flex-end'};
        align-items: ${this.position === 'top' ? 'flex-start' : this.position === 'bottom' ? 'flex-end' : 'stretch'};
      }
      
      .drawer-overlay {
        position: absolute;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.5);
        opacity: 0;
        transition: opacity 0.3s;
      }
      
      :host([open]) .drawer-overlay {
        opacity: 1;
      }
      
      .drawer-content {
        position: relative;
        z-index: 2;
        background-color: #fff;
        width: ${this.position === 'top' || this.position === 'bottom' ? '100%' : this.drawerWidth};
        height: ${this.position === 'left' || this.position === 'right' ? '100%' : 'auto'};
        max-height: ${this.position === 'top' || this.position === 'bottom' ? '80vh' : '100%'};
        display: grid;
        grid-template-rows: auto 1fr auto;
        overflow-y: auto;
        transform: ${
          this.position === 'right' ? 'translateX(100%)' :
          this.position === 'left' ? 'translateX(-100%)' :
          this.position === 'top' ? 'translateY(-100%)' :
          'translateY(100%)'
        };
        transition: transform 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
      }
      
      :host([open]) .drawer-content {
        transform: translate(0);
      }
      
      .drawer-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        border-bottom: 1px solid #e0e0e0;
      }
      
      .drawer-close-btn {
        background: none;
        border: none;
        font-size: 1em;
        line-height: 1;
        cursor: pointer;
        padding: 0.5rem;
        transition: transform 0.3s, color 0.3s;
      }

      .drawer-close-btn:hover {
        transform: scale(1.2) rotate(90deg);
        transform-origin: center;
        color: red;
      }
      
      .drawer-body {
        overflow-y: auto;
        padding: 1rem;
      }

      .drawer-footer {
        padding: 0.8rem 1rem;
        border-top: 1px solid #e0e0e0;
      }

    `;
  }
  
  // Public methods
  
  /**
   * Open the drawer
   */
  open() {
    this.setAttribute('open', '');
    this.contentElement.setAttribute('aria-hidden', 'false');

    if (this.lockBodyScroll && typeof bodyScrollLock !== 'undefined') {
      bodyScrollLock.disableBodyScroll(document.body);
    }
    
    // Dispatch open event
    this.dispatchEvent(new CustomEvent('drawer-opened', {
      bubbles: true,
      composed: true
    }));
  }
  
  /**
   * Close the drawer
   */
  close() {
    this.removeAttribute('open');
    this.contentElement.setAttribute('aria-hidden', 'true');
    
    if (this.lockBodyScroll && typeof bodyScrollLock !== 'undefined') {
      bodyScrollLock.enableBodyScroll(document.body);
    } 
    
    // Dispatch close event
    this.dispatchEvent(new CustomEvent('drawer-closed', {
      bubbles: true,
      composed: true
    }));
  }
  
  /**
   * Toggle the drawer open/closed state
   */
  toggle() {
    if (this.hasAttribute('open')) {
      this.close();
    } else {
      this.open();
    }
  }
}

// Register the custom element
if (!customElements.get('sidebar-drawer')) {
  customElements.define('sidebar-drawer', SidebarDrawer);
}

